import React from 'react';
import PropTypes from 'prop-types';
import CoachingReview from 'records/CoachingReview';
import Button from '@material-ui/core/Button';
import './UpcomingAgendaCard.styl';
import CoachingPlaylistActions from 'actions/coaching/CoachingPlaylistActions';
import {navigateToCallsTab} from 'utils/navigateToCallsTab';
import {browserHistory} from 'react-router';
import classNames from 'classnames';
import UpcomingAgendaHeader from 'components/dashboard/UpcomingAgendaHeader';
import User from 'records/User';
import pluralize from 'pluralize';
import UpcomingReviewsActions from 'actions/coaching/UpcomingReviewsActions';
import numToWords from 'utils/numToWords';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import buildUrl from 'components/dashboard/buildUrl';
import {RepProfileTab} from '@store/coaching-plans';
import {CoachingReviewTypeDisplayAndChooser} from 'components/dashboard/common/CoachingReviewTypeDisplayAndChooser';
import AgendaMeetingLink from 'components/dashboard/AgendaMeetingLink';
import AgendaStartOrContinueMeetingButton from 'components/dashboard/AgendaStartOrContinueMeetingButton';
import {HISTORY_ACTIONS} from 'components/StatsPage2.utils';
import CogIcon from 'components/pure/utils/icon/CogIcon';
import {CautionTriangleIcon} from '@components/icon/CautionTriangleIcon';
import {CircledCheckIcon} from '@components/icon/CircledCheckIcon';
import {lightTheme} from '@components/@theme/lightTheme';

const pendingClass = Object.freeze({
  READY: 'ready-border',
  TODO: 'coach-todo-border',
  OTHER_TODO: 'rep-todo-border',
});

/**
 * Used for rep profile and admin/manager sidebar
 */
export default class UpcomingAgendaCard extends React.PureComponent {
  getContentComponent(status) {
    // eslint-disable-next-line
    switch (status) {
      case 'READY':
        return ReadyTodo;
      case 'TODO':
        return Todo;
      case 'OTHER_TODO':
        return OtherTodo;
      default:
        return console.error('Invalid content status: ', status);
    }
  }

  render() {
    const {
      review,
      reviewTypes,
      horizontal,
      repView: propRepView,
      coach,
      coachId,
      otherUserView,
      onSetReviewType,
    } = this.props;

    if (!coach) {
      return null;
    }

    // if we were passed repView we're displaying on rep level.
    // otherwise check if we're on a coach dash and
    // we're the participant on the plan
    const displayAsParticipant = propRepView || review.rep.id === coachId;

    const user = displayAsParticipant ? review.rep : coach;
    const otherUser = displayAsParticipant ? coach : review.rep;
    const todoStatus = review.todoStatus();

    const Content = this.getContentComponent(todoStatus);

    return (
      <div
        className={classNames(
          'upcoming-agenda-card',
          pendingClass[todoStatus],
          horizontal && 'horizontal'
        )}>
        <UpcomingAgendaHeader {...this.props} otherUser={otherUser} />
        {!otherUserView && !displayAsParticipant && (
          <CoachingReviewTypeDisplayAndChooser
            review={review}
            reviewTypes={reviewTypes}
            onSetReviewType={onSetReviewType}
          />
        )}
        <div className="todo-section">
          <Content
            {...this.props}
            displayAsParticipant={displayAsParticipant}
            user={user}
            otherUser={otherUser}
          />
        </div>
      </div>
    );
  }
}

function TodoActions({
  label,
  onClick,
  review,
  openModifyDialog,
  disabled,
  tooltip,
  displayAsParticipant,
  children,
  showStartOrContinueButton = false,
}) {
  const button = showStartOrContinueButton ? (
    <AgendaStartOrContinueMeetingButton review={review} />
  ) : (
    <Button
      variant="outlined"
      className="ev-button"
      fullWidth
      onClick={onClick}
      disabled={disabled}>
      {label}
    </Button>
  );

  return (
    <div>
      <div className="todo-actions">
        {tooltip ? <GenericTooltip tooltip={tooltip}>{button}</GenericTooltip> : button}
        {!displayAsParticipant && <CogIcon onClick={() => openModifyDialog(review)} />}
      </div>
      {children}
    </div>
  );
}

function Todo({displayAsParticipant, user, otherUserView, review, openModifyDialog}) {
  const {todo} = review;
  const addCalls = todo.callsToAdd;
  const feedbackCalls = Math.max(todo.callsToComment.size, todo.callsToScore.size);
  const recipient = displayAsParticipant
    ? review.coachingPlan.recipient
    : review.coachingPlan.coach;
  const hasCalls = addCalls > 0 || feedbackCalls > 0;
  const hasQuestions = todo.requiredUnansweredQuestions > 0 || todo.optionalUnansweredQuestions > 0;
  const actionLabel = hasCalls
    ? 'Open Smart Assistant'
    : hasQuestions
    ? 'Answer Questions'
    : 'Preview Meeting';

  return (
    <div className="coach-todo todo-content">
      <div>
        <div className="todo-header">
          <div className="icon-wrapper">
            <CautionTriangleIcon />
          </div>
          <h4>{otherUserView ? user.firstNamePossessive : 'Your'} Action Required</h4>
        </div>
        <ol>
          {todo.pickReviewType && <li>Select a review type.</li>}
          {addCalls > 0 && (
            <li>
              Find {numToWords(addCalls)} more {pluralize('call', addCalls)} to discuss during the
              meeting.
            </li>
          )}
          {feedbackCalls > 0 && <li>{feedbackPhrase(todo, recipient)}.</li>}
          {unansweredQuestionsPhraseListItem(todo, true)}
          {unansweredQuestionsPhraseListItem(todo, false)}
        </ol>
      </div>
      {!otherUserView && (
        <TodoActions
          displayAsParticipant={displayAsParticipant}
          review={review}
          openModifyDialog={openModifyDialog}
          label={actionLabel}
          onClick={() => {
            if (hasCalls) {
              // User has calls, so we'll give them the playlist.
              CoachingPlaylistActions.setAddCallsMode({
                planId: review.planId,
                recipient: review.rep,
              });
              navigateToCallsTab();
            } else {
              // No calls to add, so just go directly to the review to start answering questions.
              browserHistory.push({
                pathname: '/metrics/profile',
                query: {
                  tab: RepProfileTab.COACHING_PLANS,
                  teamId: review.rep.teamId,
                  userId: review.rep.id,
                  planId: review.planId,
                  reviewId: review.id,
                  autoStartReview: true,
                },
              });
            }
          }}
        />
      )}
    </div>
  );
}

function OtherTodo({
  displayAsParticipant,
  otherUser,
  review,
  openModifyDialog,
  sessionUser,
  otherUserView,
}) {
  const reminderDisabled = !review.canSendReminder();
  const reminderWarning =
    review.reminderSentAt && `Reminder sent at ${review.reminderSentAt.format('h:mma')}`;

  const coachAction = (
    <TodoActions
      displayAsParticipant={displayAsParticipant}
      tooltip={reminderDisabled && <p>Reminders can only be sent once every 24 hours.</p>}
      disabled={reminderDisabled}
      review={review}
      openModifyDialog={openModifyDialog}
      label={reminderDisabled ? reminderWarning : 'Send Reminder'}
      onClick={() => UpcomingReviewsActions.sendReminder(sessionUser.id, review.id)}>
      <AgendaMeetingLink review={review} />
    </TodoActions>
  );

  const repAction = (
    <TodoActions
      displayAsParticipant={displayAsParticipant}
      openModifyDialog={openModifyDialog}
      review={review}
      label="Preview Meeting"
      onClick={() => {
        browserHistory.push({
          pathname: '/metrics/profile',
          query: {
            tab: RepProfileTab.COACHING_PLANS,
            teamId: review.rep.teamId,
            userId: review.rep.id,
            planId: review.planId,
            reviewId: review.id,
          },
        });
      }}
    />
  );

  return (
    <div className="rep-todo todo-content">
      <div>
        <div className="todo-header">
          <div className="icon-wrapper">
            <CautionTriangleIcon className="caution-rep" />
          </div>
          <h4>{otherUser.firstNamePossessive} Action Required</h4>
        </div>
        <p>
          {otherUser.firstName} needs to{' '}
          {todoPhrase(review.otherParticipantTodo, review.coachingPlan.recipient)}.
        </p>
      </div>
      {!otherUserView && (displayAsParticipant ? repAction : coachAction)}
    </div>
  );
}

function ReadyTodo({
  openModifyDialog,
  review,
  otherUserView,
  user,
  coachId,
  managerId,
  adminId,
  fromPath,
  displayAsParticipant,
}) {
  const coachAction = (
    <TodoActions
      displayAsParticipant={displayAsParticipant}
      openModifyDialog={openModifyDialog}
      review={review}
      showStartOrContinueButton
      onClick={() => {
        const state = fromPath
          ? {
              pathname: fromPath,
              action: HISTORY_ACTIONS.PUSH,
            }
          : {};
        browserHistory.push({
          pathname: buildUrl({adminId, managerId, coachId}) + '/coaching-plans',
          state,
          query: {
            repId: review.rep.id,
            planId: review.planId,
            reviewId: review.id,
            autoStartReview: true,
          },
        });
      }}
    />
  );

  const repAction = (
    <TodoActions
      displayAsParticipant={displayAsParticipant}
      openModifyDialog={openModifyDialog}
      review={review}
      label="Preview Meeting"
      onClick={() => {
        browserHistory.push({
          pathname: '/metrics/profile',
          query: {
            tab: RepProfileTab.COACHING_PLANS,
            teamId: review.rep.teamId,
            userId: review.rep.id,
            planId: review.planId,
            reviewId: review.id,
          },
        });
      }}
    />
  );

  return (
    <div className="ready-todo todo-content">
      <div>
        <div className="ready-content">
          <div className="icon-wrapper">
            <CircledCheckIcon fill={lightTheme.palette.success.main} className="icon check" />
          </div>
          {otherUserView ? (
            <div>{user.firstName} is ready for meeting.</div>
          ) : (
            <div>Ready for meeting.</div>
          )}
        </div>
      </div>
      {!otherUserView && (displayAsParticipant ? repAction : coachAction)}
    </div>
  );
}

function feedbackPhrase(todo, recipient) {
  // We add callsToAdd here to include implied todo items for unadded calls
  const scores = todo.callsToScore.size + (recipient.prepShouldScore ? todo.callsToAdd : 0);
  const comments = todo.callsToComment.size + (recipient.prepShouldComment ? todo.callsToAdd : 0);

  if (scores && comments) {
    const calls = Math.max(scores, comments);
    return calls === 1
      ? `score and comment on one call`
      : `add comments and scores to ${numToWords(calls)} calls`;
  }
  if (scores) {
    return scores === 1 ? `score one call` : `add scores to ${numToWords(scores)} calls`;
  }
  if (comments) {
    return comments === 1 ? `comment on one call` : `add comments to ${numToWords(comments)} calls`;
  }
}

function todoPhrase(todo, recipient) {
  if (todo.pickReviewType) {
    return 'select a meeting type';
  }
  const calls = todo.callsToAdd;
  if (calls) {
    return `add ${numToWords(calls)} more ${pluralize('call', calls)}`;
  }
  const feedback = feedbackPhrase(todo, recipient);
  const requiredQuestions = unansweredQuestionPhrase(todo, true);
  const optionalQuestions = unansweredQuestionPhrase(todo, false);
  const items = [feedback, requiredQuestions, optionalQuestions].filter(item => item);
  if (items.length === 1) {
    return items[0];
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }
  if (items.length === 3) {
    return `${items[0]}, ${items[1]}, and ${items[2]}`;
  }
}

function unansweredQuestionsPhraseListItem(todo, required) {
  const phrase = unansweredQuestionPhrase(todo, required);
  if (phrase) {
    return <li>{phrase}</li>;
  }
}

function unansweredQuestionPhrase(todo, required) {
  const {requiredUnansweredQuestions, optionalUnansweredQuestions} = todo;
  const count = required ? requiredUnansweredQuestions : optionalUnansweredQuestions;
  if (count > 0) {
    const adjective = required ? 'required' : 'optional';
    const noun = pluralize('question', count);
    return `answer ${numToWords(count)} ${adjective} ${noun}`;
  }
}

UpcomingAgendaCard.propTypes = {
  adminId: PropTypes.string,
  managerId: PropTypes.string,
  coachId: PropTypes.string,
  openModifyDialog: PropTypes.func, // () => void
  otherUserView: PropTypes.bool,
  repView: PropTypes.bool,
  horizontal: PropTypes.bool,
  coach: PropTypes.instanceOf(User),
  review: PropTypes.instanceOf(CoachingReview),
  sessionUser: PropTypes.instanceOf(User),
};
